/**
 * A run in progress, small enough to write to localStorage every few seconds.
 *
 * This is deliberately *not* part of `SaveData`. That blob is the lifetime
 * record — settings, totals, every note ever read — and losing it costs the
 * player everything they have ever done. A run snapshot has the opposite
 * lifetime: written a couple of hundred times a run and deleted the moment the
 * run ends. Keeping them in separate keys means a snapshot a future build
 * cannot read costs one run rather than a year of settings, and the version
 * number here can move without touching theirs.
 *
 * Nothing in here describes the building. The floor is regenerated from `seed`
 * and `levelIndex`, which is the same trick the multiplayer protocol leans on:
 * generation is deterministic, so the number a level came out of *is* the
 * level, and a snapshot stays a few hundred bytes however large the maze is.
 *
 * The one exception is the fog of war, which *is* here, run-length encoded.
 * Measured on a real Level 0 the sheet is one byte per cell, 140x140 = 19,600
 * of them raw — but a map is mostly one long run of nothing, so RLE'd it is
 * around 0.7 KB for a floor just arrived on and 4.2 KB for one crossed end to
 * end. That is worth paying: the map is the only record a player keeps of where
 * they have been, and handing back a run with a blank one is handing back
 * somewhere they have never been.
 */

/**
 * Bumped whenever a field changes meaning. A snapshot written by a different
 * number is thrown away rather than guessed at: half-understood state is worse
 * than a lost run, because a lost run is at least honest about itself.
 */
export const SNAPSHOT_VERSION = 1;

/** Deepest descent index a snapshot may claim. Anything past this is corruption. */
const MAX_LEVEL_INDEX = 4096;

/**
 * One inventory slot as stored.
 *
 * `kind` is plain text and not `ItemKind` on purpose: it came off disk, it may
 * name an item this build no longer has, and pretending otherwise here would
 * push the lie all the way into `itemDef`. `game.ts` checks it against the live
 * item table before restoring anything.
 */
export interface SavedSlot {
  kind: string;
  count: number;
}

/** Everything needed to put a player back roughly where they were. */
export interface RunSnapshot {
  version: number;
  /** `Date.now()` at the write. Only ever used to tell the player how long ago. */
  savedAt: number;

  /** The run's seed. Every floor is derived from this and a descent index. */
  seed: number;
  /** Descent index. Not a level id — the descent is unbounded and repeats. */
  levelIndex: number;
  /**
   * The seed the floor was actually generated from, and the spec it used.
   *
   * Both are redundant — `game.ts` derives the seed from `seed` and
   * `levelIndex` — and that is exactly why they are here. If a build changes
   * how floor seeds are derived, or reorders the levels, these stop matching
   * what regeneration produces, and that is the one case where the saved
   * position is a coordinate in a building that no longer exists.
   */
  levelSeed: number;
  specId: string;

  /** Seconds this run has been going. Feeds the lifetime "longest" record. */
  elapsed: number;

  x: number;
  z: number;
  yaw: number;
  pitch: number;

  health: number;
  sanity: number;

  /** 0..100. Only meaningful when a flashlight is in `inventory`. */
  flashlightCharge: number;
  /** True if the beam was lit. Coming back into the dark you left is unkind. */
  flashlightOn: boolean;

  /** `LevelSpec.id`s this run has been to, so slides still prefer somewhere new. */
  visited: string[];
  /** Note ids read this run. See the journal note in `game.ts`. */
  notesRead: string[];
  /**
   * `ItemSpot.id`s already picked up **on this floor**. Without it, closing the
   * tab in a looted room and coming back is a way to loot it again.
   *
   * Only the current floor: the descent never goes back up, so a floor left
   * behind can never be re-entered and its supplies cost nothing to forget.
   */
  takenItems: number[];
  inventory: SavedSlot[];
  /**
   * The map's fog for the current floor, run-length encoded by `encodeFog`, or
   * `''` when there is none to keep. Only the floor being stood on: the descent
   * never goes back up, so the map of a floor left behind can never be looked
   * at again.
   */
  fog: string;
}

/* ------------------------------------------------------------------ *
 * Fog of war
 * ------------------------------------------------------------------ */

/**
 * Beyond this many characters the fog is dropped rather than stored.
 *
 * A real floor crossed end to end encodes to about 4 KB. Ten times that is not
 * a map, it is a grid the size of a car park or a corrupt array, and neither is
 * worth a fifth of the localStorage budget.
 */
const FOG_MAX_CHARS = 40_000;

/** Highest cell count a stored fog may claim to cover. */
const FOG_MAX_CELLS = 1 << 20;

/**
 * `value:count` pairs, comma separated: `0:517,3:12,1:4`.
 *
 * A map is mostly one enormous run of unseen cells, so this collapses hard —
 * and staying text keeps the snapshot one readable JSON blob rather than JSON
 * with a base64 payload glued to the side of it.
 */
export function encodeFog(fog: Uint8Array): string {
  if (fog.length === 0 || fog.length > FOG_MAX_CELLS) return '';
  const parts: string[] = [];
  let value = fog[0]!;
  let run = 1;
  for (let i = 1; i <= fog.length; i++) {
    // One past the end closes the last run without repeating the push below.
    const next = i < fog.length ? fog[i]! : -1;
    if (next === value) {
      run++;
      continue;
    }
    parts.push(`${value}:${run}`);
    value = next;
    run = 1;
  }
  const out = parts.join(',');
  return out.length > FOG_MAX_CHARS ? '' : out;
}

/**
 * The inverse, refusing anything it does not fully understand.
 *
 * `cells` is what the caller expects; a fog that decodes to a different size is
 * a map of a different building and is thrown away rather than stretched.
 */
export function decodeFog(raw: unknown, cells: number): Uint8Array | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > FOG_MAX_CHARS) return null;
  if (!Number.isInteger(cells) || cells <= 0 || cells > FOG_MAX_CELLS) return null;
  const out = new Uint8Array(cells);
  let at = 0;
  for (const part of raw.split(',')) {
    const colon = part.indexOf(':');
    if (colon <= 0) return null;
    const value = Number(part.slice(0, colon));
    const run = Number(part.slice(colon + 1));
    if (!Number.isInteger(value) || value < 0 || value > 255) return null;
    if (!Number.isInteger(run) || run <= 0 || at + run > cells) return null;
    if (value !== 0) out.fill(value, at, at + run);
    at += run;
  }
  return at === cells ? out : null;
}

/* ------------------------------------------------------------------ *
 * Encoding
 * ------------------------------------------------------------------ */

/**
 * Round to `places` decimals.
 *
 * Position is stored to the centimetre and angles to about a fifth of a degree,
 * which is far below what anyone could notice on being put back down and saves
 * roughly a third of the JSON — a float like 41.283746528193 is thirteen
 * characters of nothing.
 */
function round(v: number, places: number): number {
  const f = 10 ** places;
  return Math.round(v * f) / f;
}

/** Compact JSON for storage. The rounding is the only lossy part. */
export function encodeSnapshot(s: RunSnapshot): string {
  const packed: RunSnapshot = {
    ...s,
    savedAt: Math.round(s.savedAt),
    elapsed: round(s.elapsed, 1),
    x: round(s.x, 2),
    z: round(s.z, 2),
    yaw: round(s.yaw, 3),
    pitch: round(s.pitch, 3),
    health: round(s.health, 1),
    sanity: round(s.sanity, 1),
    flashlightCharge: round(s.flashlightCharge, 1),
  };
  return JSON.stringify(packed);
}

/* ------------------------------------------------------------------ *
 * Decoding
 * ------------------------------------------------------------------ */

/** A finite number inside `[min, max]`, or null. NaN and Infinity fail here. */
function num(v: unknown, min: number, max: number): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return v >= min && v <= max ? v : null;
}

function int(v: unknown, min: number, max: number): number | null {
  const n = num(v, min, max);
  return n !== null && Number.isInteger(n) ? n : null;
}

/** Non-empty text, capped so a corrupt blob cannot smuggle a novel back in. */
function text(v: unknown, maxLength: number): string | null {
  return typeof v === 'string' && v.length > 0 && v.length <= maxLength ? v : null;
}

function textList(v: unknown, maxItems: number, maxLength: number): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const entry of v) {
    const s = text(entry, maxLength);
    if (s !== null && !out.includes(s)) out.push(s);
    if (out.length >= maxItems) break;
  }
  return out;
}

function intList(v: unknown, maxItems: number, max: number): number[] {
  if (!Array.isArray(v)) return [];
  const out: number[] = [];
  for (const entry of v) {
    const n = int(entry, 0, max);
    if (n !== null) out.push(n);
    if (out.length >= maxItems) break;
  }
  return out;
}

function slots(v: unknown): SavedSlot[] {
  if (!Array.isArray(v)) return [];
  const out: SavedSlot[] = [];
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null) continue;
    const e = entry as { kind?: unknown; count?: unknown };
    const kind = text(e.kind, 40);
    const count = int(e.count, 1, 999);
    if (kind === null || count === null) continue;
    out.push({ kind, count });
    // Well past `TUNING.survival.inventorySlots`; this is a corruption stop,
    // not a rule about how much anyone may carry.
    if (out.length >= 64) break;
  }
  return out;
}

/**
 * Parse and validate. Returns null for anything this build does not fully
 * understand — a wrong version, a missing field, a number that is not a number.
 *
 * Every field is checked rather than trusted, because the failure this guards
 * against is not a hostile blob, it is *last month's build*: a snapshot whose
 * shape moved under it must never reach the game as a half-restored run.
 */
export function decodeSnapshot(raw: string | null): RunSnapshot | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const s = parsed as Record<string, unknown>;

  if (s.version !== SNAPSHOT_VERSION) return null;

  const seed = int(s.seed, 0, 0xffffffff);
  const levelIndex = int(s.levelIndex, 0, MAX_LEVEL_INDEX);
  const levelSeed = int(s.levelSeed, 0, 0xffffffff);
  const specId = text(s.specId, 64);
  const x = num(s.x, -1e5, 1e5);
  const z = num(s.z, -1e5, 1e5);
  // A dead player has no run to go back to, so zero health is corruption by the
  // time it reaches here: `game.ts` clears the snapshot on death.
  const health = num(s.health, 0.01, 100);
  const sanity = num(s.sanity, 0, 100);
  if (
    seed === null ||
    levelIndex === null ||
    levelSeed === null ||
    specId === null ||
    x === null ||
    z === null ||
    health === null ||
    sanity === null
  ) {
    return null;
  }

  return {
    version: SNAPSHOT_VERSION,
    savedAt: num(s.savedAt, 0, Number.MAX_SAFE_INTEGER) ?? 0,
    seed,
    levelIndex,
    levelSeed,
    specId,
    // A run that has somehow gone for a week is not worth refusing to restore.
    elapsed: num(s.elapsed, 0, 1e7) ?? 0,
    x,
    z,
    yaw: num(s.yaw, -Math.PI * 2, Math.PI * 2) ?? 0,
    pitch: num(s.pitch, -Math.PI, Math.PI) ?? 0,
    health,
    sanity,
    flashlightCharge: num(s.flashlightCharge, 0, 100) ?? 0,
    flashlightOn: s.flashlightOn === true,
    visited: textList(s.visited, 256, 64),
    notesRead: textList(s.notesRead, 256, 64),
    takenItems: intList(s.takenItems, 512, 100000),
    inventory: slots(s.inventory),
    // Kept as written and validated at the point of use, where the cell count
    // of the regenerated floor is finally known. See `decodeFog`.
    fog: typeof s.fog === 'string' && s.fog.length <= FOG_MAX_CHARS ? s.fog : '',
  };
}
